import { useCallback, useEffect, useMemo, useState } from "react";

import { MinusCircleFilled, PlusCircleOutlined } from "@ant-design/icons";
import { ConditionalType } from "@regal-voice/shared-types";
import { Form, Input, Popover, Select, Typography } from "antd";

import {
    RVCollapse,
    RVIcon,
    RVIconSizeOptions,
    RVFormFloatingLabelWrap,
    RVFormItem,
    RVButton,
    RVColumn,
    RVRow,
    ConfigurableVariableOption,
    ConfigurableVariablesPickerPlugin,
    JSONMenuTriggerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
    HANDLEBAR_TRIGGERS,
    HANDLEBARS_WRAP,
} from "@regal-voice/ui";
import {
    generateFormItemRequiredRules,
    generateJsonValidator,
} from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import {
    createOptionsWithPrefixWithOriginalFieldName,
    toContactsFieldLookup,
} from "ServerData/EventSchema/Utils/eventSchemaUtils";
import { useFlags } from "Services/FeatureFlagService";
import { useListBrandSecretsQuery } from "Services/restApi/brandSecretsApi";
import { NodeTriggerType } from "Types/Journey";

import { useQueryContactAttributes } from "../../../../../shared/hooks/useQueryContactAttributes";
import { useQueryEventAttributes } from "../../../../../shared/hooks/useQueryEventAttributes";

import type { BrandSecret } from "Services/BrandSecrets/BrandSecretsApiService";

import styles from "./WebhookFormItems.module.scss";

const HANDLEBARS_REGEX_LIST = [HANDLEBARS_WRAP];

const WEBHOOK_METHOD_HAS_JSON_PAYLOAD = new Set<string>(["POST", "PUT", "PATCH"]);

const JSONValidator = generateJsonValidator("JSON is not in a valid format");

const WEBHOOK_FORM_FIELDS = {
    name: "webhookName",
    url: "webhookUrl",
    method: "webhookMethod",
    headers: "webhookHeaders",
    secureHeaders: "secureWebhookHeaders",
    jsonPayload: "webhookJsonPayload",
} as const;

const HEADER_ROW_FIELDS = {
    header: "header",
    value: "value",
} as const;

const SECRET_ROW_FIELDS = {
    header: "header",
    secretId: "secretId",
    prefix: "prefix",
} as const;

const REQUIRED_WEBHOOK_NAME_RULES = generateFormItemRequiredRules("Webhook name", false, false);
const REQUIRED_WEBHOOK_URL_RULES = generateFormItemRequiredRules("Endpoint URL", false, false);
const REQUIRED_WEBHOOK_METHOD_RULES = generateFormItemRequiredRules("Webhook method", false, false);
const REQUIRED_WEBHOOK_PAYLOAD_RULES = generateFormItemRequiredRules("Webhook payload", false, false);
const REQUIRED_HEADER_NAME_RULES = generateFormItemRequiredRules("Header name", false, false);
const REQUIRED_HEADER_VALUE_RULES = generateFormItemRequiredRules("Header value", false, false);
const REQUIRED_SECRET_RULES = generateFormItemRequiredRules("Secret", false, false);

const normalizeHeaderName = (name: string) => name.trim().toLowerCase();

const duplicatePlainHeadersValidator = async (_: unknown, headersKeyValuePairs: { header: string }[]) => {
    if (!headersKeyValuePairs?.length) {
        return;
    }
    const seen = new Set<string>();
    for (const pair of headersKeyValuePairs) {
        const key = normalizeHeaderName(pair?.header ?? "");
        if (seen.has(key)) {
            return Promise.reject(new Error("There are multiple headers with same name"));
        }
        seen.add(key);
    }
};

export function WebhookFormItems({
    triggeringEvent,
    formValues,
}: {
    triggeringEvent: { eventType?: NodeTriggerType | ConditionalType; eventName?: string };
    formValues: Record<string, unknown>;
}): JSX.Element {
    const { journeyBuilderWebhookSecretHeaders } = useFlags();
    const webhookMethod = formValues?.webhookMethod as string;
    const showWebhookJsonPayload = WEBHOOK_METHOD_HAS_JSON_PAYLOAD.has(webhookMethod);

    // @TODO replace with useContactAttributesFullListQuery, no need for the useEffect
    // @see src/ServerData/EventSchema/Hooks/useContactAttributesQuery/useContactAttributesQuery.ts
    /* Getting the contact fields */
    const { loadContactAttributes, contactAttributes } = useQueryContactAttributes({
        formatToObject: false,
    });

    useEffect(() => {
        loadContactAttributes();
        // We only want to load the contact attributes once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contactEventOptions = useMemo(() => toContactsFieldLookup(contactAttributes), [contactAttributes]);

    /* Getting the event fields */
    const { loading: eventLoading, eventAttributes } = useQueryEventAttributes(triggeringEvent);
    const eventOptions = useMemo(() => {
        if (eventLoading || eventAttributes.length === 0) {
            return [];
        }
        return eventAttributes.map((event) => createOptionsWithPrefixWithOriginalFieldName(event, "event"));
    }, [eventLoading, eventAttributes]);

    const allOptions = useMemo<Array<ConfigurableVariableOption>>(
        () =>
            [...contactEventOptions, ...eventOptions].map(
                (option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)
            ),
        [contactEventOptions, eventOptions]
    );

    const {
        data: secretsData,
        isLoading: secretsLoading,
        isError: secretsError,
    } = useListBrandSecretsQuery(undefined, { skip: !journeyBuilderWebhookSecretHeaders });
    const secrets: BrandSecret[] = secretsData?.items ?? [];

    const form = Form.useFormInstance();
    const watchedPlainHeaders = Form.useWatch(WEBHOOK_FORM_FIELDS.headers, form) as Array<unknown> | undefined;
    const watchedSecureHeaders = Form.useWatch(WEBHOOK_FORM_FIELDS.secureHeaders, form) as Array<unknown> | undefined;

    const hasExistingHeaders = (watchedPlainHeaders?.length ?? 0) > 0 || (watchedSecureHeaders?.length ?? 0) > 0;

    const [activeKeys, setActiveKeys] = useState<string[]>(hasExistingHeaders ? ["headers"] : []);

    useEffect(() => {
        if (hasExistingHeaders && activeKeys.length === 0) {
            setActiveKeys(["headers"]);
        }
        // Only auto-open; user closing the panel should not be overridden
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasExistingHeaders]);

    const uniqueAcrossBothListsValidator = useCallback(
        (_: unknown, secureHeaders: Array<{ header: string }>) => {
            const plainHeaders: Array<{ header: string }> = form.getFieldValue(WEBHOOK_FORM_FIELDS.headers) ?? [];
            const plainNames = new Set(
                plainHeaders
                    .map((h) => h?.header)
                    .filter(Boolean)
                    .map(normalizeHeaderName)
            );
            const allSecureNames = (secureHeaders ?? [])
                .map((h) => h?.header)
                .filter(Boolean)
                .map(normalizeHeaderName);
            const seenSecure = new Set<string>();
            for (const name of allSecureNames) {
                if (seenSecure.has(name) || plainNames.has(name)) {
                    return Promise.reject(new Error("There are multiple headers with the same name"));
                }
                seenSecure.add(name);
            }
            return Promise.resolve();
        },
        [form]
    );

    const headersContent = (
        <Form.List name={WEBHOOK_FORM_FIELDS.headers} rules={[{ validator: duplicatePlainHeadersValidator }]}>
            {(fields, { add, remove }, { errors }) => (
                <>
                    {fields.map((field) => (
                        <RVRow key={field.key} className={styles.formRow} alignItems="center" gap={2}>
                            <RVColumn className={styles.formRowInputs}>
                                <RVFormFloatingLabelWrap
                                    {...field}
                                    name={[field.name, HEADER_ROW_FIELDS.header]}
                                    placeholder="Header"
                                    rules={REQUIRED_HEADER_NAME_RULES}
                                >
                                    <Input />
                                </RVFormFloatingLabelWrap>
                                <RVFormFloatingLabelWrap
                                    {...field}
                                    name={[field.name, HEADER_ROW_FIELDS.value]}
                                    placeholder="Value"
                                    rules={REQUIRED_HEADER_VALUE_RULES}
                                >
                                    <Input />
                                </RVFormFloatingLabelWrap>
                            </RVColumn>
                            <MinusCircleFilled onClick={() => remove(field.name)} className={styles.removeItem} />
                        </RVRow>
                    ))}
                    {errors.length > 0 && <Form.ErrorList errors={errors} />}

                    <RVFormItem className={styles.addHeaderButton}>
                        <RVButton icon={<PlusCircleOutlined />} onClick={() => add()} block>
                            Text Header
                        </RVButton>
                    </RVFormItem>
                </>
            )}
        </Form.List>
    );

    return (
        <>
            <RVFormFloatingLabelWrap
                name={WEBHOOK_FORM_FIELDS.name}
                label="Name of Webhook"
                showFormLabel={true}
                placeholder="Webhook Name"
                rules={REQUIRED_WEBHOOK_NAME_RULES}
            >
                <Input />
            </RVFormFloatingLabelWrap>
            <RVFormFloatingLabelWrap
                name={WEBHOOK_FORM_FIELDS.url}
                label="Endpoint URL"
                showFormLabel={true}
                placeholder="https://yourcompany.com/callback"
                rules={[
                    ...REQUIRED_WEBHOOK_URL_RULES,
                    {
                        validator: async (_, url: string) => {
                            const urlRegexValidator =
                                /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)(?:\.(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)*(?:\.(?:[a-z¡-￿]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;
                            if (!urlRegexValidator.test(url)) {
                                return Promise.reject(new Error("Endpoint url format is incorrect"));
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
            >
                <WebhookUrlEditor allOptions={allOptions} loading={eventLoading} />
            </RVFormFloatingLabelWrap>
            <RVFormFloatingLabelWrap
                name={WEBHOOK_FORM_FIELDS.method}
                label="Method"
                showFormLabel={true}
                placeholder="Select Method"
                rules={REQUIRED_WEBHOOK_METHOD_RULES}
            >
                <Select>
                    <Select.Option key={"webhook-method-get"} value="GET">
                        GET
                    </Select.Option>
                    <Select.Option key={"webhook-method-post"} value="POST">
                        POST
                    </Select.Option>
                    <Select.Option key={"webhook-method-put"} value="PUT">
                        PUT
                    </Select.Option>
                    <Select.Option key={"webhook-method-patch"} value="PATCH">
                        PATCH
                    </Select.Option>
                </Select>
            </RVFormFloatingLabelWrap>
            {journeyBuilderWebhookSecretHeaders ? (
                <RVCollapse
                    className={styles.collapsePanel}
                    expandIcon={({ isActive }) => {
                        const icon: string = isActive ? "arrow-down" : "arrow-right";
                        return <RVIcon icon={icon} size={RVIconSizeOptions.XSMALL} />;
                    }}
                    expandIconPosition="end"
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(typeof keys === "string" ? [keys] : (keys ?? []))}
                    panelsList={[
                        {
                            header: "Custom Headers (Optional)",
                            key: "headers",
                            forceRender: true,
                            showArrow: true,
                            content: (
                                <>
                                    {headersContent}
                                    {secretsError && (
                                        <Typography.Text className={styles.sectionLabel}>
                                            Failed to load secrets. Please refresh and try again.
                                        </Typography.Text>
                                    )}
                                    <Form.List
                                        name={WEBHOOK_FORM_FIELDS.secureHeaders}
                                        rules={[{ validator: uniqueAcrossBothListsValidator }]}
                                    >
                                        {(fields, { add, remove }, { errors }) => (
                                            <>
                                                {fields.map((field) => (
                                                    <SecretHeaderRow
                                                        key={field.key}
                                                        field={field}
                                                        remove={remove}
                                                        secrets={secrets}
                                                        secretsLoading={secretsLoading}
                                                        secretsError={secretsError}
                                                    />
                                                ))}
                                                <Form.ErrorList errors={errors} />
                                                <RVFormItem className={styles.addHeaderButton}>
                                                    <RVButton
                                                        icon={<PlusCircleOutlined />}
                                                        onClick={() => add()}
                                                        block
                                                        disabled={secretsLoading || secretsError}
                                                    >
                                                        Secret Header
                                                    </RVButton>
                                                </RVFormItem>
                                            </>
                                        )}
                                    </Form.List>
                                </>
                            ),
                        },
                    ]}
                />
            ) : (
                <RVFormItem label="Custom Headers (Optional)">{headersContent}</RVFormItem>
            )}
            {showWebhookJsonPayload && (
                <RVFormItem
                    name={WEBHOOK_FORM_FIELDS.jsonPayload}
                    label={
                        <span className={styles.title}>
                            JSON Payload
                            <Popover
                                placement={"top"}
                                title={"Dynamic References"}
                                content={
                                    <div className={styles.smallPopover}>
                                        Use{" "}
                                        <span className={styles.exampleHandlebars}>
                                            &#123;&#123;handle bars&#125;&#125;
                                        </span>{" "}
                                        to add references from the contact profile, triggering event or a previous
                                        webhook response. For contact attributes, prepend with <strong>contact.</strong>{" "}
                                        For triggering event properties, prepend with <b>event.</b> For a webhook
                                        response, prepend with webhook_ID such as <b>webhook_1, webhook_2.</b>{" "}
                                        <a
                                            className="external-link"
                                            target="_blank"
                                            href="https://support.regal.ai/hc/en-us/articles/5725272620187-How-to-Use-Journey-Webhooks"
                                            rel="noreferrer"
                                        >
                                            More Details
                                        </a>
                                    </div>
                                }
                            >
                                <RVIcon className={styles.infoIcon} icon="info-circle" size={RVIconSizeOptions.SMALL} />
                            </Popover>
                        </span>
                    }
                    rules={[...REQUIRED_WEBHOOK_PAYLOAD_RULES, { validator: JSONValidator }]}
                >
                    <WebhookJsonPayloadEditor allOptions={allOptions} loading={eventLoading} />
                </RVFormItem>
            )}
        </>
    );
}

type SecretHeaderRowProps = {
    field: { key: number; name: number };
    remove: (name: number) => void;
    secrets: BrandSecret[];
    secretsLoading: boolean;
    secretsError: boolean;
};

function SecretHeaderRow({ field, remove, secrets, secretsLoading, secretsError }: SecretHeaderRowProps) {
    const form = Form.useFormInstance();
    const savedSecretId = Form.useWatch(
        [WEBHOOK_FORM_FIELDS.secureHeaders, field.name, SECRET_ROW_FIELDS.secretId],
        form
    );
    const isDeleted = savedSecretId && !secretsLoading && !secretsError && !secrets.some((s) => s.id === savedSecretId);

    return (
        <RVRow className={styles.formRow} alignItems="center" gap={2}>
            <RVColumn className={styles.formRowInputs}>
                <RVFormFloatingLabelWrap
                    name={[field.name, SECRET_ROW_FIELDS.header]}
                    placeholder="Header"
                    rules={REQUIRED_HEADER_NAME_RULES}
                >
                    <Input />
                </RVFormFloatingLabelWrap>
                <RVFormFloatingLabelWrap
                    name={[field.name, SECRET_ROW_FIELDS.secretId]}
                    placeholder="Secret"
                    rules={REQUIRED_SECRET_RULES}
                >
                    <Select loading={secretsLoading} disabled={secretsLoading || secretsError}>
                        {isDeleted && (
                            <Select.Option value={savedSecretId} disabled className={styles.deletedSecretOption}>
                                [Deleted secret]
                            </Select.Option>
                        )}
                        {secrets.map((s) => (
                            <Select.Option key={s.id} value={s.id}>
                                {s.name}
                            </Select.Option>
                        ))}
                    </Select>
                </RVFormFloatingLabelWrap>
                <RVFormFloatingLabelWrap name={[field.name, SECRET_ROW_FIELDS.prefix]} placeholder="Prefix (Optional)">
                    <Input />
                </RVFormFloatingLabelWrap>
            </RVColumn>
            <MinusCircleFilled onClick={() => remove(field.name)} className={styles.removeItem} />
        </RVRow>
    );
}

type WebhookEditorProps = {
    allOptions: Array<ConfigurableVariableOption>;
    loading: boolean;
};

function WebhookUrlEditor({ allOptions, loading }: WebhookEditorProps) {
    const form = Form.useFormInstance();
    const initialValue = form.getFieldValue("webhookUrl");

    const handleChange = useCallback(
        (newValue: string) => {
            form.setFieldsValue({ webhookUrl: newValue });
        },
        [form]
    );

    return (
        <PlainTextEditor
            data-testid="webhook-url-lexical-editor"
            className={styles.urlEditorContainer}
            inputClassName={styles.urlEditorInput}
            value={initialValue}
            onChange={handleChange}
            singleRow
        >
            <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
            <ConfigurableVariablesPickerPlugin
                popAbove
                fixedWidth
                loading={loading}
                options={allOptions}
                prioritizedTriggers={HANDLEBAR_TRIGGERS}
            />
        </PlainTextEditor>
    );
}

function WebhookJsonPayloadEditor({ allOptions, loading }: WebhookEditorProps) {
    const form = Form.useFormInstance();
    const initialValue = form.getFieldValue("webhookJsonPayload");

    const handleChange = useCallback(
        (newValue: string) => {
            form.setFieldsValue({ webhookJsonPayload: newValue });
        },
        [form]
    );

    return (
        <PlainTextEditor
            data-testid="webhook-json-payload-lexical-editor"
            className={styles.jsonEditorContainer}
            inputClassName={styles.jsonEditorInput}
            value={initialValue}
            onChange={handleChange}
        >
            <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
            <ConfigurableVariablesPickerPlugin
                popAbove
                fixedWidth
                loading={loading}
                options={allOptions}
                prioritizedTriggers={HANDLEBAR_TRIGGERS}
            />
            <JSONMenuTriggerPlugin />
        </PlainTextEditor>
    );
}
